import { FC, useEffect, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
// components
import { PriceDisplay } from 'dibs-buyer-price';
import { PriceDisplayVariant } from 'dibs-buyer-price/exports/PriceDisplayVariant';
import { OriginalListPriceLazy } from 'dibs-auctions/exports/OriginalListPrice';
import { PdpRespTitle } from '../PdpRespTitle/PdpRespTitle';
import { PdpSharedPrivateOfferBanner } from '../PdpSharedPrivateOfferBanner/PdpSharedPrivateOfferBanner';
import { PdpSharedSidebarFavorites } from '../shared/PdpSharedSidebarFavorites/PdpSharedSidebarFavorites';
import { PdpSharedShippingBadgeLazy } from '../PdpSharedShippingBadge/PdpSharedShippingBadgeLazy';
import { PdpSharedFramingStatusLazy } from '../PdpSharedFramingStatus/PdpSharedFramingStatusLazy';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import { PdpSharedCondensedSkuAttributes } from '../PdpSharedCondensedSkuAttributes/PdpSharedCondensedSkuAttributes';
import { PdpSharedCondensedSidebarButtons } from '../PdpSharedCondensedSidebarButtons/PdpSharedCondensedSidebarButtons';
import { PdpSharedBlurbLeadTime } from '../PdpSharedBlurbs/PdpSharedBlurbLeadTime';
import { PdpSharedAuctionCountdownLazy } from '../PdpSharedAuctionCountdown/PdpSharedAuctionCountdownLazy';
import { PdpSharedCondensedPriceDisplay } from '../PdpSharedCondensedAuctionPriceDisplay/PdpSharedCondensedAuctionPriceDisplay';
import { BrowserHistoryContextProvider } from '../../global/BrowserHistoryContext/BrowserHistoryContext';
import { PdpRespSharedHeroCarousel } from '../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel';

// Helpers
import { getSelectedSku } from '../helpers/getSelectedSku';
import { useSharedAuctionContext } from '../PdpSharedAuctionContext/PdpSharedAuctionContext';
import { useAppSelector } from '../hooks/useReactReduxHooks';
import {
    usePdpPricingOptimizationVariant,
    useTrackPdpPricingOptimization,
} from '../hooks/usePdpPricingOptimizationVariant';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

// styles
import styles from './PdpRespCondensedListedContent.scss';

// types
import type { PdpRespCondensedListedContent_viewer$key } from './__generated__/PdpRespCondensedListedContent_viewer.graphql';
import type { PdpRespCondensedListedContent_user$key } from './__generated__/PdpRespCondensedListedContent_user.graphql';
import type { PdpRespCondensedListedContent_item$key } from './__generated__/PdpRespCondensedListedContent_item.graphql';

type Props = {
    currency: string;
    selectedSkuId?: string;
    viewer: PdpRespCondensedListedContent_viewer$key;
    user?: PdpRespCondensedListedContent_user$key;
    item: PdpRespCondensedListedContent_item$key;
};

export const PdpRespCondensedListedContent: FC<Props> = ({
    item: itemRef,
    user: userRef,
    viewer: viewerRef,
    currency,
    selectedSkuId,
}) => {
    const viewer = useFragment(
        graphql`
            fragment PdpRespCondensedListedContent_viewer on Viewer {
                showHeartCount: featureFlag(feature: "pdpDisplayHeartCount")
                ...PdpSharedShippingBadge_viewer
                ...PdpSharedPromoCode_viewer
                ...PdpSharedCondensedSidebarButtons_viewer
                ...PdpRespSharedHeroCarousel_viewer
            }
        `,
        viewerRef
    );
    const user = useFragment(
        graphql`
            fragment PdpRespCondensedListedContent_user on User {
                ...PdpSharedCondensedSidebarButtons_user
                ...PdpSharedPromoCode_user
                ...PdpRespSharedHeroCarousel_user
            }
        `,
        userRef || null
    );
    const item = useFragment(
        graphql`
            fragment PdpRespCondensedListedContent_item on Item {
                firstPhotoWebPath(size: small)
                isAuctionEnabled
                title
                displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                    ctas {
                        showAcceptOffer
                    }
                    ...PdpSharedPromoCode_displayInfo
                    ...PdpSharedCondensedSidebarButtons_displayInfo
                }
                seller {
                    isAccessible
                }
                skus {
                    serviceId
                    ...PdpSharedBlurbLeadTime_sku
                    ...PriceDisplay_sku
                        @include(if: $isMultiSku)
                        @arguments(
                            hasSkuId: true
                            userId: $userId
                            isTrade: $isTrade
                            userCountryCode: $userCountryCode
                            priceBookName: $priceBookName
                        )
                    ... on ItemSkuType @include(if: $isMultiSku) {
                        displayPrice(
                            isTrade: $isTrade
                            buyerId: $userId
                            userCountryCode: $userCountryCode
                            priceBookName: $priceBookName
                        ) {
                            ...PriceDisplayVariant_displayPrice
                        }
                    }
                }
                ...PriceDisplay_item
                    @include(if: $afterDisplayOrBuaSsr)
                    @arguments(
                        userId: $userId
                        isTrade: $isTrade
                        userCountryCode: $userCountryCode
                        priceBookName: $priceBookName
                    )
                ... on Item @include(if: $afterDisplayOrBuaSsr) {
                    ...PriceDisplayVariant_item
                    displayPrice(
                        isTrade: $isTrade
                        buyerId: $userId
                        userCountryCode: $userCountryCode
                        priceBookName: $priceBookName
                    ) {
                        ...PriceDisplayVariant_displayPrice
                    }
                }
                ...PdpSharedCondensedAuctionPriceDisplay @include(if: $fetchAuction)
                ...PdpSharedCondensedSkuAttributes_item @include(if: $isMultiSku)
                ...PdpSharedShippingBadge_item
                ...PdpSharedFramingStatus_item @include(if: $isClient)
                ...PdpRespTitle_item
                ...PdpSharedCondensedSidebarButtons_item
                ...PdpSharedBlurbLeadTime_item
                ...PdpSharedSidebarFavorites_item
                ...PdpSharedAuctionCountdownLazy_item @include(if: $fetchAuction)
                ...OriginalListPriceLazy_item @include(if: $fetchAuction)
                ...PdpRespSharedHeroCarousel_item
                ...usePdpPricingOptimizationVariant_item
            }
        `,
        itemRef
    );

    const [hasFreeShipping, setHasFreeShipping] = useState(false);
    const relayVariables = useAppSelector(state => state.relayVariables.variables);
    const { userId, itemId, isClient, isMultiSku, fetchAuction, afterDisplayOrBuaSsr } =
        relayVariables;
    const displayInfo = item?.displayInfo || null;
    const isSellerAccessible = item?.seller?.isAccessible;
    const selectedSku = getSelectedSku(item, selectedSkuId || '');
    const isAuctionEnabled = item?.isAuctionEnabled;
    const { setShouldDisplayCountdown } = useSharedAuctionContext();
    const showAcceptOffer = displayInfo?.ctas?.showAcceptOffer;

    useEffect(() => {
        if (isAuctionEnabled) {
            setShouldDisplayCountdown(true);
        }
    }, [setShouldDisplayCountdown, isAuctionEnabled]);

    const isPdpPricingOptimizationVariant = usePdpPricingOptimizationVariant(item);
    useTrackPdpPricingOptimization(item);
    const displayPrice = (selectedSku?.displayPrice || item.displayPrice)?.filter(filterNulls);

    return (
        <div
            className={classnames(styles.contentWrapper, {
                [styles.withBottomPadding]: !isSellerAccessible,
            })}
        >
            <div data-tn="condensed-image-carousel" className={styles.contentColumn}>
                {/* Image Carousel */}
                <BrowserHistoryContextProvider>
                    <PdpRespSharedHeroCarousel
                        viewer={viewer}
                        item={item}
                        user={user}
                        currency={currency}
                        afterDisplay={isClient}
                        size="xSmall"
                    />
                </BrowserHistoryContextProvider>
            </div>

            {/* Sidebar */}
            <div className={styles.contentColumn}>
                {/* Auction timer */}
                {fetchAuction && <PdpSharedAuctionCountdownLazy item={item} />}
                <div className={styles.titleFavoritesWrapper}>
                    {/* Title */}
                    <PdpRespTitle item={item} />
                    {/* Favorite / Folder */}
                    <PdpSharedSidebarFavorites
                        heartIconWrapperClass={styles.heartIconWrapper}
                        itemImage={item.firstPhotoWebPath || ''}
                        item={item}
                        showHeartCount={viewer.showHeartCount}
                        isClient={!!isClient}
                        userId={userId}
                        wrapperClass={styles.favoritesWrapper}
                        fetchFolder
                    />
                </div>
                {isClient && showAcceptOffer && (
                    <PdpSharedPrivateOfferBanner className={styles.privateOfferContainer} />
                )}
                {fetchAuction && (
                    <OriginalListPriceLazy
                        item={item}
                        currency={currency || ''}
                        className={styles.originalListPrice}
                    />
                )}
                {/* Shipping Badge */}
                {isClient && (
                    <div
                        className={classnames(styles.shippingBadgeContainer, {
                            [styles.noMargin]: !hasFreeShipping,
                        })}
                    >
                        <PdpSharedShippingBadgeLazy
                            item={item}
                            viewer={viewer}
                            setHasFreeShipping={setHasFreeShipping}
                        />
                    </div>
                )}
                {/* Price */}
                <div className={styles.priceWithAccessoryInlineContainer}>
                    {fetchAuction && (
                        <PdpSharedCondensedPriceDisplay
                            currency={currency || ''}
                            isMultiSku={!!isMultiSku}
                            item={item}
                            selectedSkuId={selectedSkuId || ''}
                        />
                    )}

                    {!isAuctionEnabled &&
                        afterDisplayOrBuaSsr &&
                        (isPdpPricingOptimizationVariant ? (
                            <PriceDisplayVariant
                                item={item}
                                displayPrice={displayPrice}
                                currency={currency}
                            />
                        ) : (
                            <PriceDisplay
                                currency={currency}
                                item={item}
                                useSvgIcon
                                sku={isMultiSku ? selectedSku : null}
                                appendElementToPrice={<PdpSharedFramingStatusLazy item={item} />}
                                displayVatLabel
                            />
                        ))}
                </div>
                {/* Promo Code */}
                <PdpSharedPromoCode
                    displayInfo={displayInfo}
                    itemId={itemId}
                    user={user}
                    userId={userId}
                    viewer={viewer}
                    currency={currency}
                    excludeShippingPromos
                />

                {isMultiSku && (
                    <PdpSharedCondensedSkuAttributes item={item} selectedSkuId={selectedSkuId} />
                )}

                {/* Item Ctas */}
                <PdpSharedCondensedSidebarButtons
                    relayVariables={{
                        userId,
                        itemId,
                        fetchAuction,
                    }}
                    user={user}
                    item={item}
                    viewer={viewer}
                    itemSearch={null}
                    displayInfo={displayInfo}
                    selectedSkuId={selectedSkuId}
                />

                <PdpSharedBlurbLeadTime sku={selectedSku} item={item} />
            </div>
        </div>
    );
};
